import { ReactNode, Suspense } from "react";

import { getSuppliedAccessoriesData } from "app/smartphone/[slug]/[reference]/SuppliedAccessoriesSection/supplied-accessories.helper";
import { getInsuranceFees } from "app/smartphone/[slug]/[reference]/insurance-fees.helper";
import BreadCrumb from "shared/BreadCrumb";
import { FunnelContextProvider } from "shared/Funnel/FunnelContextProvider";
import { AppLayout } from "shared/Layout";
import { OtherDevices } from "shared/OtherDevices";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { OthersContextProvider } from "shared/catalog/OthersCatalog/OthersContext";
import { getOthersEntries } from "shared/catalog/OthersCatalog/others-catalog.helper";
import { ProductPageContextProvider } from "shared/product/ProductPageContext";
import { ProductPageSeo } from "shared/product/ProductPageSeo/ProductPageSeo";
import {
  getProductAverageRating,
  getProductPageSeo,
} from "shared/product/ProductPageSeo/product-seo.helper";
import { ProductSection } from "shared/product/ProductSection";
import { getProductSectionData } from "shared/product/ProductSection/product-section.helper";
import { CACHE_ONE_HOUR } from "utils/config.utils";
import { initializeApollo } from "utils/with-apollo.utils";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { withSeo } from "utils/with-seo.utils";

import { AdvantagesSection } from "./AdvantagesSection";
import { DetailsSection } from "./DetailsSection";
import { InsuranceSection } from "./InsuranceSection";
import { RatingsSection } from "./RatingsSection";
import { RentalQuestions } from "./RentalQuestions";
import { SuppliedAccessoriesSection } from "./SuppliedAccessoriesSection";
import { TipsSection } from "./TipsSection";
import {
  GetProductPagesSlugDocument,
  GetProductPagesSlugQuery,
} from "./get-product-pages-slug.query.generated";

export type ProductPageProps = Exclude<
  Awaited<ReturnType<typeof getStaticProps>>,
  { redirect: { destination: string } }
>["props"];

export const ProductPage = ({
  productData,
  productAverageRating,
  othersEntries,
  insuranceFees,
  productSlug,
  referenceSlug,
  settings,
}: ProductPageProps) => {
  const i18n = useI18n();

  return (
    <>
      <ProductPageSeo
        productData={productData}
        productAverageRating={productAverageRating}
      />

      <ProductPageContextProvider
        productData={productData}
        referenceSlug={referenceSlug}
        settings={settings}
      >
        <BreadCrumb
          className="!absolute z-20"
          parts={[
            { label: i18n.t("breadcrumb.home"), to: "/" },
            { label: i18n.t("breadcrumb.catalog"), to: "/smartphone" },
            {
              label: i18n.t("breadcrumb.brand", {
                brand:
                  productData.family.brand === "Apple"
                    ? "iPhone"
                    : productData.family.brand,
              }),
              to:
                productData.family.brand === "Apple"
                  ? "/smartphone/iphone"
                  : "/smartphone/android",
            },
            {
              label: productData.family.modelName,
              to: `/smartphone/${productSlug}`,
            },
          ]}
        />

        <FunnelContextProvider>
          <ProductSection />
        </FunnelContextProvider>

        <Suspense>
          <SuppliedAccessoriesSection />
          <AdvantagesSection productData={productData} />
          <DetailsSection productData={productData} />
          <InsuranceSection insuranceFees={insuranceFees} />
          <RatingsSection productData={productData} />
          {productData.family.tips?.length ? (
            <TipsSection />
          ) : (
            <div className="h-10" />
          )}
          <OthersContextProvider entries={othersEntries}>
            <OtherDevices />
          </OthersContextProvider>
          <RentalQuestions />
        </Suspense>
      </ProductPageContextProvider>
    </>
  );
};

ProductPage.getLayout = (page: ReactNode, pageProps: ProductPageProps) => (
  <>
    <AppLayout withBanner {...pageProps}>
      {page}
    </AppLayout>

    <div className="h-24" />
  </>
);

export const getStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data }: { data: GetProductPagesSlugQuery } = await apolloClient.query(
    {
      query: GetProductPagesSlugDocument,
      fetchPolicy: "network-only",
    },
  );

  const variantEntries =
    data?.b2CSmartphoneCatalog?.data?.attributes?.entries
      ?.map((entry) => entry?.subscriptions?.data)
      .flat(1)
      .map(
        (subscription) =>
          subscription?.attributes?.smartphoneVariant?.data?.attributes,
      )
      .filter(Boolean) ?? [];

  const paths = variantEntries.map((smartphoneVariant, index) => {
    const slug = smartphoneVariant?.smartphoneFamily?.data?.attributes?.slug;
    const reference = smartphoneVariant?.slug;

    if (typeof slug !== "string" || typeof reference !== "string") {
      throw new Error(
        `getStaticPaths(/smartphone/[slug]/[reference]) - invalid variant at index ${index} of [${variantEntries
          .map((e) => e?.slug)
          .join(",")}]: ${JSON.stringify(smartphoneVariant, null, 2)}`,
      );
    }

    return { params: { slug, reference } };
  });

  return { paths, fallback: "blocking" };
};

type GetStaticPropsParams = {
  params: { slug: string; reference: string };
};

export const getStaticProps = async ({
  params: { reference: referenceSlug, slug: productSlug },
}: GetStaticPropsParams) => {
  const productData = await getProductSectionData(productSlug);

  // variants is undefined, product probably doesn't exist
  if (!productData.variants) {
    return {
      redirect: {
        destination: "/smartphone",
      },
    };
  }

  const subscriptionSku = productData.variants?.find(
    ({ variantSlug }) => variantSlug === referenceSlug,
  )?.sku;

  // subscription (variant) does not exist, redirect to product
  if (!subscriptionSku) {
    return {
      redirect: {
        destination: `/smartphone/${productSlug}`,
      },
    };
  }

  const suppliedAccessories = !!subscriptionSku
    ? await getSuppliedAccessoriesData(subscriptionSku)
    : null;

  const othersEntries = await getOthersEntries();

  const productAverageRating = await getProductAverageRating();

  const insuranceFees = await getInsuranceFees(
    productData.variants?.[0]?.subscriptionModelName,
  );

  const translations = await fetchTranslations([
    "product",
    "marquee",
    "home",
    "catalog",
    "breadcrumb",
  ]);

  return {
    props: {
      productData: {
        ...productData,
        suppliedAccessories,
      },
      productAverageRating,
      othersEntries,
      translations,
      insuranceFees,
      seo: await getProductPageSeo(productData, referenceSlug, productSlug),
      settings: await fetchSettings(),
      productSlug,
      referenceSlug,
    },
    revalidate: CACHE_ONE_HOUR,
  };
};

export default withApp(ProductPage, [withSeo]);
